import {
  Auth0Client,
  GenericError,
  MissingScopesError,
  type Auth0ClientOptions,
  type ConnectAccountRedirectResult,
  type GetTokenSilentlyVerboseResponse,
  type MyAccountApiClient,
  type RedirectLoginOptions,
  type RedirectLoginResult,
} from '@auth0/auth0-spa-js';
import { computed, effect, inject, Injectable, signal } from '@angular/core';

import { UsersApi } from '../api/users.api';
import { ENVIRONMENT } from '../config/environment.token';
import { StorageService } from '../platform/storage.service';
import type { Account, Auth0User } from './auth.types';
import { MyAccountNotGrantedError } from './my-account-not-granted.error';

const ACTIVE_ACCOUNT_STORAGE_KEY = 'growzz:active_account_id';

const MY_ACCOUNT_SCOPES = [
  'read:me:authentication_methods',
  'create:me:authentication_methods',
  'update:me:authentication_methods',
  'delete:me:authentication_methods',
];

const REAUTHENTICATION_ERRORS = new Set([
  'login_required',
  'mfa_required',
  'consent_required',
  'interaction_required',
  'invalid_grant',
  'missing_refresh_token',
  'unmet_authentication_requirements',
]);

interface RedirectAppState {
  readonly targetUrl?: string;
  readonly myAccount?: boolean;
}

export type RestoredSession = 'authenticated' | 'signed-out' | 'expired';

/**
 * Wrapper around `@auth0/auth0-spa-js` exposing signals instead of
 * promises-of-state. Mirrors the behaviour of the legacy AuthService
 * (growzz-web-app-legacy/app/components/login/auth.service.js).
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly env = inject(ENVIRONMENT);
  private readonly storage = inject(StorageService);
  private readonly users = inject(UsersApi);

  private readonly client = new Auth0Client(this.clientOptions());
  private myAccountTokenInFlight: Promise<void> | null = null;
  private myAccountRedirectCompleted = false;
  private activeAccountResolveInFlight: Promise<number | null> | null = null;
  private accountsInFlight: Promise<readonly Account[]> | null = null;

  private readonly _isAuthenticated = signal(false);
  private readonly _accessToken = signal<string | null>(null);
  private readonly _auth0User = signal<Auth0User | null>(null);
  private readonly _activeAccountId = signal<number | null>(this.readStoredAccount());
  private readonly _accounts = signal<readonly Account[] | null>(null);
  private readonly _redirectResult = signal<
    RedirectLoginResult<RedirectAppState> | ConnectAccountRedirectResult<RedirectAppState> | null
  >(null);

  readonly isAuthenticated = this._isAuthenticated.asReadonly();
  readonly accessToken = this._accessToken.asReadonly();
  readonly auth0User = this._auth0User.asReadonly();
  readonly activeAccountId = this._activeAccountId.asReadonly();
  readonly redirectResult = this._redirectResult.asReadonly();

  readonly myAccount: MyAccountApiClient = this.client.myAccount;

  readonly email = computed(() => this._auth0User()?.email ?? null);
  readonly orgClaim = computed(() => this._auth0User()?.org_id ?? null);

  /** Every Growzz account the signed-in user belongs to (empty until loaded). */
  readonly accounts = computed<readonly Account[]>(() => this._accounts() ?? []);

  /** The account matching `activeAccountId`, once the accounts list is loaded. */
  readonly activeAccount = computed<Account | null>(() => {
    const id = this._activeAccountId();
    if (id === null) return null;
    return this._accounts()?.find((account) => account.id === id) ?? null;
  });

  /** Whether the user can switch between more than one account. */
  readonly hasMultipleAccounts = computed(() => this.accounts().length > 1);

  constructor() {
    effect(() => {
      const id = this._activeAccountId();
      if (id === null) {
        this.storage.remove(ACTIVE_ACCOUNT_STORAGE_KEY);
      } else {
        this.storage.set(ACTIVE_ACCOUNT_STORAGE_KEY, String(id));
      }
    });
  }

  /**
   * Restore an existing Auth0 session if one is present.
   *
   * Returns once Auth0 has reported whether the user is authenticated and,
   * if so, after we have an access token + user profile cached locally.
   */
  async restoreSession(): Promise<RestoredSession> {
    if (!(await this.client.isAuthenticated())) {
      this.markSignedOut();
      return 'signed-out';
    }

    try {
      const [token, user] = await Promise.all([
        this.client.getTokenSilently(),
        this.client.getUser<Auth0User>(),
      ]);
      this._accessToken.set(token ?? null);
      this._auth0User.set(user ?? null);
      this._isAuthenticated.set(true);
      return 'authenticated';
    } catch (err) {
      if (!requiresReauthentication(err)) throw err;
      this.markSignedOut();
      return 'expired';
    }
  }

  /**
   * Handle the `?code=...&state=...` query params after Auth0 redirects back
   * to `/login/callback`. Caches the result for restoreSession() to pick up.
   */
  async handleRedirectCallback(): Promise<
    RedirectLoginResult<RedirectAppState> | ConnectAccountRedirectResult<RedirectAppState>
  > {
    const result = await this.client.handleRedirectCallback<RedirectAppState>();
    this._redirectResult.set(result);
    if (result.appState?.myAccount) {
      this.myAccountRedirectCompleted = true;
    }
    await this.restoreSession();
    return result;
  }

  loginWithRedirect(
    options: {
      targetUrl?: string;
      organizationId?: string;
      connection?: string;
      invitation?: string;
      /** Force the Auth0 login screen even when a session cookie exists. */
      promptLogin?: boolean;
    } = {},
  ): Promise<void> {
    const redirectOptions: RedirectLoginOptions<RedirectAppState> = {
      appState: { targetUrl: options.targetUrl ?? '/' },
      authorizationParams: {
        ...(options.organizationId ? { organization: options.organizationId } : {}),
        ...(options.connection ? { connection: options.connection } : {}),
        ...(options.invitation ? { invitation: options.invitation } : {}),
        ...(options.promptLogin ? { prompt: 'login' } : {}),
      },
    };
    this.reloadOnBfcacheRestore();
    return this.client.loginWithRedirect(redirectOptions);
  }

  async getFreshToken(): Promise<string | null> {
    try {
      const token = (await this.client.getTokenSilently()) ?? null;
      this._accessToken.set(token);
      return token;
    } catch {
      this._accessToken.set(null);
      this._isAuthenticated.set(false);
      return null;
    }
  }

  /**
   * Must resolve before parallel `myAccount` calls: each call asks for its own
   * scope, and without one cached token covering all of them they race
   * separate refresh-token rotations.
   */
  warmMyAccountToken(): Promise<void> {
    this.myAccountTokenInFlight ??= this.fetchMyAccountToken().finally(() => {
      this.myAccountTokenInFlight = null;
    });
    return this.myAccountTokenInFlight;
  }

  hasCompletedMyAccountRedirect(): boolean {
    return this.myAccountRedirectCompleted;
  }

  authorizeMyAccountWithRedirect(targetUrl: string): Promise<void> {
    const organization = this.orgClaim();
    this.reloadOnBfcacheRestore();
    return this.client.loginWithRedirect<RedirectAppState>({
      appState: { targetUrl, myAccount: true },
      authorizationParams: {
        audience: this.myAccountAudience(),
        scope: MY_ACCOUNT_SCOPES.join(' '),
        ...(organization ? { organization } : {}),
      },
    });
  }

  setActiveAccount(account: Account | number | null): void {
    if (account === null) {
      this._activeAccountId.set(null);
      return;
    }
    const id = typeof account === 'number' ? account : account.id;
    this._activeAccountId.set(id);
  }

  /**
   * Ensures `activeAccountId` is set when we can infer it from the session.
   *
   * Mirrors legacy `restoreSession` → match Auth0 `org_id` to
   * `account.organization_id`, or pick the sole account when there is only
   * one. Without this, org-based logins never populate `growzz:active_account_id`
   * and account-scoped checks (including account admin) stay false.
   */
  async ensureActiveAccount(): Promise<number | null> {
    if (!this._isAuthenticated()) {
      return null;
    }

    // When Auth0 issued an org-scoped token, its `org_id` claim is the source
    // of truth and may differ from the account id still in storage after the
    // user switched accounts (a switch hops through Auth0 for the new org).
    // In that case fall through and reconcile against the org claim. Without an
    // org claim, trust a previously-selected account id to avoid a refetch.
    const existing = this._activeAccountId();
    if (existing !== null && this.orgClaim() === null) {
      return existing;
    }

    if (this.activeAccountResolveInFlight) {
      return this.activeAccountResolveInFlight;
    }
    this.activeAccountResolveInFlight = this.resolveActiveAccountFromApi();
    try {
      return await this.activeAccountResolveInFlight;
    } finally {
      this.activeAccountResolveInFlight = null;
    }
  }

  /**
   * Fetch (once) and cache the accounts the user belongs to. Shared by the
   * top-nav account label, the account switcher screen, and active-account
   * resolution so they don't each hit `/users/me?expand[]=accounts`.
   */
  async ensureAccountsLoaded(): Promise<readonly Account[]> {
    // Trust a non-empty cache, but treat an empty list as "not loaded yet" so a
    // later call refetches. Account membership syncs eventually — right after
    // signup the backend can serve a stale `[]` while it waits for the Auth0
    // webhook. Caching that empty result would strand a freshly-invited user
    // without their account for the whole session; refetching lets it self-heal
    // once the membership lands. Concurrent calls stay de-duplicated below.
    const cached = this._accounts();
    if (cached !== null && cached.length > 0) {
      return cached;
    }
    if (this.accountsInFlight) {
      return this.accountsInFlight;
    }
    this.accountsInFlight = this.users
      .meWithAccounts()
      .then((me) => {
        const accounts = me.accounts ?? [];
        this._accounts.set(accounts);
        return accounts;
      })
      .catch((err) => {
        console.error('[auth] ensureAccountsLoaded failed', err);
        throw err;
      })
      .finally(() => {
        this.accountsInFlight = null;
      });
    return this.accountsInFlight;
  }

  /**
   * Drop the local Auth0 session without opening the hosted logout page.
   * Used when tokens are rejected by the API so the user can sign in again
   * from the intro screen instead of being bounced straight to Auth0.
   */
  async clearSessionLocally(): Promise<void> {
    this._isAuthenticated.set(false);
    this._accessToken.set(null);
    this._auth0User.set(null);
    this._activeAccountId.set(null);
    this._accounts.set(null);
    this._redirectResult.set(null);
    await this.client.logout({
      logoutParams: {
        returnTo: `${this.runtimeOrigin()}/login`,
      },
      openUrl: false,
    });
  }

  async logout(): Promise<void> {
    this._isAuthenticated.set(false);
    this._accessToken.set(null);
    this._auth0User.set(null);
    this._activeAccountId.set(null);
    this._accounts.set(null);
    this._redirectResult.set(null);
    this.reloadOnBfcacheRestore();
    await this.client.logout({
      logoutParams: {
        returnTo: this.runtimeOrigin(),
      },
    });
  }

  /**
   * Arm a reload for when this document is later restored from the browser's
   * back/forward cache.
   *
   * Handing off to Auth0 (login, account switch, logout) is a full document
   * navigation, and pressing browser Back can resurrect this document from
   * bfcache with its entire JS heap frozen mid-hand-off: signals, the Auth0
   * client cache, and any in-flight UI (e.g. the account switcher's spinner
   * on the newly picked account while the checkmark still marks the old one)
   * no longer match what the completed redirect wrote to storage. Reloading
   * on restore re-bootstraps from storage, making Back equivalent to the
   * fresh navigation it appears to be.
   */
  private reloadOnBfcacheRestore(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener(
      'pageshow',
      (event) => {
        if (event.persisted) window.location.reload();
      },
      { once: true },
    );
  }

  /**
   * Resolve the origin the SPA is actually served from at runtime.
   *
   * On Netlify deploy-previews the hostname differs from the static
   * `appBaseUrl` baked into the environment file. Using `location.origin`
   * ensures Auth0 redirect / logout URLs always point back to the current
   * host rather than the canonical Netlify site URL.
   */
  private runtimeOrigin(): string {
    return typeof window !== 'undefined' ? window.location.origin : this.env.appBaseUrl;
  }

  private clientOptions(): Auth0ClientOptions {
    return {
      domain: this.env.auth0.domain,
      clientId: this.env.auth0.clientId,
      authorizationParams: {
        audience: this.env.auth0.audience,
        redirect_uri: `${this.runtimeOrigin()}/login/callback`,
      },
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useMrrt: true,
    };
  }

  private myAccountAudience(): string {
    return `https://${this.env.auth0.domain}/me/`;
  }

  private async fetchMyAccountToken(): Promise<void> {
    if (missingMyAccountScopes(await this.requestMyAccountToken('on')).length === 0) return;
    // An interactively issued token is cached under the scopes it requested, not
    // the ones Auth0 granted, so a token minted before the grant was fixed keeps
    // being served from cache. Refreshing under the same key overwrites it.
    const missing = missingMyAccountScopes(await this.requestMyAccountToken('off'));
    if (missing.length === 0) return;
    throw this.myAccountRedirectCompleted
      ? new MyAccountNotGrantedError(missing)
      : new MissingScopesError(this.myAccountAudience(), missing.join(' '));
  }

  private requestMyAccountToken(
    cacheMode: 'on' | 'off',
  ): Promise<GetTokenSilentlyVerboseResponse | undefined> {
    return this.client.getTokenSilently({
      cacheMode,
      detailedResponse: true,
      authorizationParams: {
        audience: this.myAccountAudience(),
        scope: MY_ACCOUNT_SCOPES.join(' '),
      },
    });
  }

  private markSignedOut(): void {
    this._isAuthenticated.set(false);
    this._accessToken.set(null);
    this._auth0User.set(null);
  }

  private readStoredAccount(): number | null {
    const raw = this.storage.get(ACTIVE_ACCOUNT_STORAGE_KEY);
    if (raw === null) return null;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  }

  private async resolveActiveAccountFromApi(): Promise<number | null> {
    try {
      const accounts = await this.ensureAccountsLoaded();
      const orgId = this.orgClaim();
      if (orgId) {
        const match = accounts.find((account) => account.organizationId === orgId);
        if (match) {
          this.setActiveAccount(match);
          return match.id;
        }
      }
      // No org match: keep a still-valid stored selection, otherwise fall back
      // to the sole account when the user only belongs to one.
      const existing = this._activeAccountId();
      if (existing !== null && accounts.some((account) => account.id === existing)) {
        return existing;
      }
      if (accounts.length === 1) {
        this.setActiveAccount(accounts[0]);
        return accounts[0].id;
      }
      // Nothing determinable (no org match, no valid stored id, not a sole
      // account) — report undetermined so the account guard prompts a choice.
      return null;
    } catch (err) {
      console.error('[auth] ensureActiveAccount failed', err);
      return this._activeAccountId();
    }
  }
}

function requiresReauthentication(error: unknown): boolean {
  return error instanceof GenericError && REAUTHENTICATION_ERRORS.has(error.error);
}

function missingMyAccountScopes(token: GetTokenSilentlyVerboseResponse | undefined): string[] {
  if (token === undefined) return MY_ACCOUNT_SCOPES;
  if (token.scope === undefined) return [];
  const granted = new Set(token.scope.split(' '));
  return MY_ACCOUNT_SCOPES.filter((scope) => !granted.has(scope));
}
